import {useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Swal from 'sweetalert2';
import {fetchPatientDetailsUuid, postPatientReport} from '../Redux/Thunk/Actions';
import '../styles/PatientReportFileTemplateCreate.scss';
import FigmaHeader from './FigmaHeader/FigmaHeader';

// Common styles
const commonStyles = {
  input: {
    border: '1px solid #b0a4a4ff',
    outline: 'none',
    padding: '10px',
    backgroundColor: 'white'
  },
  label: {
    fontWeight: 600
  },
  separator: {
    fontWeight: 600,
    fontSize: '18px'
  },
  section: {
    maxWidth: '97%'
    // margin: '0 auto'
  }
};

// Reusable FormField component
const FormField = ({label, type = 'text', name, value, onChange, show, options = [], width = '200px', labelWidth = '160px', reference}) => {
  const inputStyle = {...commonStyles.input, width};

  if (show) {
    // Display mode (read-only)
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${labelWidth} 16px 1fr`,
          alignItems: 'center',
          gap: '16px'
        }}>
        <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
        <span style={commonStyles.separator}>:</span>
        <span style={{fontSize: '14px', textAlign: 'left'}}>{value}</span>
      </div>
    );
  }

  // Edit mode
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${labelWidth} 16px ${width}`,
        alignItems: 'center',
        gap: '16px'
      }}>
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
      <span style={commonStyles.separator}>:</span>

      {/* Input for editing mode */}
      {type === 'select' ? (
        <select name={name} value={value} onChange={onChange} style={inputStyle}>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} style={inputStyle} />
      )}
    </div>
  );
};

// FormRow for grid layout with columns
const FormRow = ({columns, show, onChange, valueChange, className = 'report-template-wrap2', columnGap = '40px', defaultLabelWidth = '160px'}) => (
  <div className={className} style={{padding: '3px 6px', display: 'flex', gap: columnGap}}>
    {columns.map((colFields, colIndex) => (
      <div
        key={colIndex}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '3px'
        }}>
        {colFields.map(field => (
          <FormField
            key={field.name}
            label={field.label}
            type={field.type || 'text'}
            name={field.name}
            value={valueChange[field.name]}
            onChange={onChange}
            show={show}
            options={field.options}
            width={field.width || '100%'}
            labelWidth={field.labelWidth || defaultLabelWidth}
            reference={field.reference}
          />
        ))}
      </div>
    ))}
  </div>
);

// MacroscopicField component for result + reference layout
const MacroscopicField = ({label, name, value, onChange, show, type = 'text', options = [], width = '200px', reference}) => {
  const inputStyle = {...commonStyles.input, width};

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 200px 160px', // Label | Input/Value | Reference
        gap: '16px',
        alignItems: 'center',
        padding: '8px 0',
        width: '100%'
      }}>
      {/* Parameter Label */}
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px', textAlign: 'left'}}>{label}</span>

      {/* Input Field or Display Value */}
      {show ? (
        // Display mode
        <span style={{fontSize: '14px', textAlign: 'left'}}>{value || ''}</span>
      ) : (
        // Edit mode
        <>
          {type === 'select' ? (
            <select name={name} value={value} onChange={onChange} style={inputStyle}>
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input type={type} name={name} value={value} onChange={onChange} style={inputStyle} />
          )}
        </>
      )}

      {/* WHO Reference (Always visible) */}
      <span style={{textAlign: 'left', fontSize: '14px', fontWeight: '500', color: '#333'}}>{reference || ''}</span>
    </div>
  );
};

// Section header component
const SectionHeader = ({title, className = 'spermiogram-head'}) => (
  <h2
    style={{
      textAlign: 'center',
      backgroundColor: '#f7f7f7',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontSize: '20px',
      fontWeight: '600',
      padding: '8px 0 8px 10px',
      margin: '8px 0'
    }}
    className={className}>
    {title}
  </h2>
);

// Column headers for tables
const TableHeaders = ({columns, colWidths = '200px 230px 200px', gapSize = '0px'}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: colWidths,
      alignItems: 'center',
      gap: gapSize,
      overflowX: 'hidden',
      maxWidth: '97%',
      margin: '0 auto',
      backgroundColor: '#e9ecef',
      padding: '8px 16px',
      borderBottom: '1px solid #ddd'
    }}>
    {columns.map((col, index) => (
      <span
        key={index}
        style={{
          fontWeight: '600',
          fontSize: '14px',
          textAlign: index === 1 ? 'center' : 'left'
        }}>
        {col}
      </span>
    ))}
  </div>
);

// MicroscopyField component for pre/post wash parameters - Consistent with FormField styling
const MicroscopyField = ({label, name, reference, show, valueChange, onChange, width = '140px'}) => {
  const preInputWidth = width; // Configurable pre-wash input width (for volume field)
  const postInputWidth = '200px'; // Standard input width for post-wash

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `240px 16px ${preInputWidth} ${postInputWidth} ${preInputWidth} ${postInputWidth} 120px`,
        gap: '8px',
        alignItems: 'center',
        padding: '8px 0',
        width: '100%'
      }}>
      {/* Parameter Label */}
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px', textAlign: 'left'}}>{label}</span>

      {/* Separator */}
      <span style={commonStyles.separator}>:</span>

      {/* Pre-wash input/display */}
      {show ? (
        // Display mode
        <span style={{width: preInputWidth, textAlign: 'center', fontSize: '14px'}}>{valueChange[name] || ''}</span>
      ) : (
        // Edit mode
        <input type='text' onChange={onChange} name={name} value={valueChange[name]} style={{...commonStyles.input, width: preInputWidth}} />
      )}

      {/* Post-wash input/display */}
      {show ? (
        // Display mode
        <span style={{width: postInputWidth, textAlign: 'center', fontSize: '14px'}}>{valueChange[`${name}Post`] || ''}</span>
      ) : (
        // Edit mode
        <input type='text' onChange={onChange} name={`${name}Post`} value={valueChange[`${name}Post`]} style={{...commonStyles.input, width: postInputWidth}} />
      )}

      {/* Pre-wash display (hidden in display mode) */}
      <span className='input hide' style={{width: preInputWidth, textAlign: 'center', fontSize: '14px'}}>
        {valueChange[name]}
      </span>

      {/* Post-wash display (hidden in display mode) */}
      <span className='input hide' style={{width: postInputWidth, textAlign: 'center', fontSize: '14px'}}>
        {valueChange[`${name}Post`]}
      </span>

      {/* WHO Reference */}
      <span style={{textAlign: 'left', fontSize: '14px', fontWeight: '500'}}>{reference || ''}</span>
    </div>
  );
};

// Microscopy fields configuration - Updated per iui.docx specification
const microscopyFields = [
  {label: 'Volume (mL)', name: 'volume', reference: '>= 1.4mL', width: '200px'},
  {label: 'Sperm Concentration (mill/mL)', name: 'concentration', reference: '>= 16 million/mL', width: '200px'},
  {label: 'Total Sperm Count (million)', name: 'total', reference: '>= 39 million', width: '200px'},
  {label: 'Progressive Motility (%)', name: 'progressiveMotility', reference: '>= 30%', width: '200px'},
  {label: 'Non-Progressive Motility (%)', name: 'nonProgressiveMotile', width: '200px'},
  {label: 'Immotile (%)', name: 'immotile', width: '200px'},
  {label: 'Total Motile Sperm Count (TMSC)', name: 'totalMotileSpermCount', reference: '>=42%', width: '200px'},
  {label: 'Morphology (Strict Criteria)', name: 'morphology', reference: '>= 4% normal forms', width: '200px'}
];

// Method dropdown options - Updated per iui.docx specification
const methodOptions = [
  {value: '', label: 'Select'},
  {value: 'DGC', label: 'DGC'},
  {value: 'DGC+SWIM UP', label: 'DGC+SWIM UP'},
  {value: 'SWIM UP', label: 'SWIM UP'},
  {value: 'MICROFLUIDCS', label: 'MICROFLUIDCS'}
];

// Patient Info Section component
const PatientInfoSection = ({patientNumber, partnerType, show, handleChange, valueChange}) => {
  const genderOptions = [
    {value: '', label: 'Select Gender'},
    {value: 'Male', label: 'Male'},
    {value: 'Female', label: 'Female'},
    {value: 'Others', label: 'Others'},
    {value: 'Decline to State', label: 'Decline to State'}
  ];

  return (
    <div className='' style={{width: '100%', display: 'block'}} id={`patientInfo${patientNumber}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '3px',
          backgroundColor: '#f7f7f7 ',
          borderRadius: '6px',
          padding: '12px 6px 12px 6px',
          gap: '3px'
        }}>
        {/* Patient ID */}
        <FormField label='Patient ID' name={`patientId${patientNumber}`} value={valueChange[`patientId${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />

        {/* Patient Name */}
        <FormField
          label={`${partnerType} Partner Name`}
          name={`patientName${patientNumber}`}
          value={valueChange[`patientName${patientNumber}`]}
          onChange={handleChange}
          show={show}
          width='250px'
          labelWidth='125px'
        />

        {/* Gender */}
        <FormField
          label='Gender'
          type='select'
          name={`gender${patientNumber}`}
          value={valueChange[`gender${patientNumber}`]}
          onChange={handleChange}
          show={show}
          options={genderOptions}
          width='250px'
          labelWidth='125px'
        />

        {/* Age */}
        <FormField label='Age' name={`age${patientNumber}`} value={valueChange[`age${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />
      </div>
    </div>
  );
};

const IUIReport = props => {
  const [show, setshow] = useState(false);
  const Content = useRef();
  const [valueChange, setvalueChange] = useState({
    gender1: 'Male',
    gender2: 'Female',
    bloodGroup1: 'A+',
    bloodGroup2: 'A+',
    coupleId: '',
    collectionDate: '',
    resultDate: '',
    requestingConsultant: '',
    procedureRequested: '',
    sample: '',
    abstinencePeriod: '',
    collectionMethod: '',
    medication: '',
    collectionDifficulty: '',
    pH: '',
    timeOfCollection: '',
    timeOfCollectionHour: '',
    timeOfCollectionMinute: '',
    timeOfCollectionPeriod: 'AM',
    timeOfExamination: '',
    timeOfExaminationHour: '',
    timeOfExaminationMinute: '',
    timeOfExaminationPeriod: 'AM',
    appearance: '',
    color: '',
    homogeneity: '',
    completeSample: '',
    liquefactionTime: '',
    volume: '',
    volumePost: '',
    concentration: '',
    concentrationPost: '',
    total: '',
    totalPost: '',
    progressiveMotility: '',
    progressiveMotilityPost: '',
    nonProgressiveMotile: '',
    nonProgressiveMotilePost: '',
    immotile: '',
    immotilePost: '',
    totalMotileSpermCount: '',
    totalMotileSpermCountPost: '',
    method: '',
    methodType: '',
    morphology: '',
    morphologyPost: '',
    diagnosis: ''
  });
  const [patientOne_UUId, setPatientOne_UUId] = useState('');
  const [patientTwo_UUId, setPatientTwo_UUId] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = e => {
    var text = e.target.value;

    if (e.target.name === 'patientId1' || e.target.name === 'patientId2') {
      if (e.target.value.length === 10 && e.target.value.charAt(0) === 'A') {
        const fetchData = async () => {
          const payload = {
            p_uhid: e.target.value
          };
          props.loaderchange('true');
          const addPatient = fetchPatientDetailsUuid(payload);
          const response = await dispatch(addPatient);
          if (response.payload?.status?.code === 200) {
            if (response.payload.data.data.length === 1) {
              const data = response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              if (e.target.name === 'patientId1') {
                setPatientOne_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patientName1: namePat1,
                  age1: data?.age,
                  gender1: data?.gender,
                  patientId1: text
                });
              }
              if (e.target.name === 'patientId2') {
                setPatientTwo_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patientName2: namePat1,
                  age2: data?.age,
                  gender2: data?.gender,
                  patientId2: text
                });
              }

              props.loaderchange('false');
            } else {
              const data = response.payload.data.data[0].gender == 'Male' ? response.payload.data.data[0] : response.payload.data.data[1];
              const data2 = response.payload.data.data[1].gender == 'Female' ? response.payload.data.data[1] : response.payload.data.data[0];
              console.log(data, data2);
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              const namePat2 = data2?.titles + ' ' + data2?.first_name + ' ' + data2?.last_name;

              setPatientOne_UUId(data?.id);
              setPatientTwo_UUId(data2?.id);
              setvalueChange({
                ...valueChange,
                patientName1: namePat1,
                patientName2: namePat2,
                age1: data?.age,
                age2: data2?.age,
                gender1: data?.gender,
                gender2: data2?.gender,
                patientId1: data?.p_uhid,
                patientId2: data2?.p_uhid
              });

              props.loaderchange('false');
            }
          } else {
            props.loaderchange('false');
            props.popupalert('true');
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert('false');
            }, 2000);
          }
          return;
        };

        fetchData();
      } else {
        if (e.target.name === 'patientId1') {
          setvalueChange({
            ...valueChange,
            patientId1: e.target.value
          });
        }
        if (e.target.name === 'patientId2') {
          setvalueChange({
            ...valueChange,
            patientId2: e.target.value
          });
        }
      }
    } else {
      setvalueChange({...valueChange, [e.target.name]: e.target.value});
    }
  };
  const handletest = async () => {
    try {
      if (
        valueChange.patientOne_UUId !== '' &&
        valueChange.patientId1 !== '' &&
        valueChange.patientId1.length === 10 &&
        (valueChange.patientId2 !== '' ? (valueChange.patientId1 !== valueChange.patientId2 ? true : false) : true)
      ) {
        setshow(true);
        // Set timeout to handle async report creation
        setTimeout(async () => {
          // Get HTML payload
          const htmlPayload = document.getElementById('reportcreate').innerHTML;

          // Prepare payload for creating report
          const payload = {
            report_template: htmlPayload,
            patient_id: patientOne_UUId,
            description: '',
            patient_two: patientTwo_UUId,
            couple_id: valueChange.coupleId,
            template_type: 'IUI Report'
          };

          // Dispatch action to create patient report
          props.loaderchange(true);
          const reportResponse = await dispatch(postPatientReport({type: '2', payload: payload}));

          if (reportResponse?.payload?.status?.code === 200) {
            props.loaderchange(false);
            navigate('/patientreports');

            // Show success message
            Swal.fire({
              title: 'Successful.',
              text: 'Report added successful.',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            });
          } else {
            setshow(false);

            props.loaderchange(false);

            // Show error message
            props.popupalert(true);
            props.popuptext('Network went wrong, Please try after some time.');

            // Hide error message after timeout
            setTimeout(() => {
              props.popupalert(false);
            }, 2000);
          }
        }, 3000);
      } else {
        // Show error message for invalid patient ids
        Swal.fire({
          title: 'Error.',
          text: 'Please enter valid patient id.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };
  const sideactive = useSelector(state => state.counter.sidebarnav);

  return (
    <>
      <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
        <div className='row'>
          <div className='col'>
            <h3 className='page-title'>Report Template</h3>
            <ul className='breadcrumb'>
              <li className='breadcrumb-item'>
                <a href='/' style={{textDecoration: 'none', color: 'black'}}>
                  Dashboard /
                </a>
                <a href='/patientreports' style={{textDecoration: 'none', color: 'black'}}>
                  &nbsp;Patient Reports
                </a>
              </li>
              <li className='breadcrumb-item active'>Report Template</li>
            </ul>
          </div>
        </div>
        <h1 style={{textAlign: 'center'}}>🧬 INTRAUTERINE INSEMINATION (IUI) REPORT</h1>
        <p style={{textAlign: 'center', fontStyle: 'italic', marginBottom: '20px'}}>As per WHO 6th Edition (2021) Guidelines</p>
        <div style={{backgroundColor: 'white', width: '100%'}} className='patientreport' id='reportcreate'>
          {/* <div style={{}}> */}
          <>
            <FigmaHeader />
          </>
          {/* Patient & Request Details */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '3px',
              border: '1px solid #000'
            }}
            className='report-template-wrap1'>
            <PatientInfoSection patientNumber={1} partnerType='Male' show={show} handleChange={handleChange} valueChange={valueChange} />
            <div className='patientreportcreate2' style={{width: '100%', height: '100%', backgroundColor: '#f7f7f7'}} id='patientInfo2'>
              <PatientInfoSection patientNumber={2} partnerType='Female' show={show} handleChange={handleChange} valueChange={valueChange} />
            </div>
          </div>
          <h2 style={{textAlign: 'center', fontWeight: '600', backgroundColor: '#f7f7f7', padding: '10px'}}>INTRAUTERINE INSEMINATION (IUI) REPORT</h2>

          <div>
            <SectionHeader title='🔬 PATIENT & REQUEST DETAILS' />
            <FormRow
              columns={[
                [
                  {label: 'Collection Date', type: 'date', name: 'collectionDate', width: '250px'},
                  {label: 'Result Date', type: 'date', name: 'resultDate', width: '250px'},
                  {label: 'Requesting Consultant', name: 'requestingConsultant', width: '250px'},
                  {label: 'Procedure Requested', name: 'procedureRequested', width: '250px'},
                  {
                    label: 'Sample',
                    type: 'select',
                    name: 'sample',
                    width: '180px',
                    options: [
                      {value: '', label: 'Select Sample'},
                      {value: 'Fresh', label: 'Fresh'},
                      {value: 'Frozen', label: 'Frozen'},
                      {value: 'fresh+Frozen', label: 'fresh+Frozen'}
                    ]
                  }
                ],
                [
                  {label: 'Collection Method', name: 'collectionMethod', width: '250px'},
                  {label: 'Abstinence Period', name: 'abstinencePeriod', width: '250px'},
                  {label: 'Medication', name: 'medication', width: '250px'},
                  {label: 'Collection Difficulty', name: 'collectionDifficulty', width: '250px'}
                ]
              ]}
              show={show}
              onChange={handleChange}
              valueChange={valueChange}
              defaultLabelWidth='200px'
            />
          </div>

          {/* Macroscopic Analysis */}
          <div style={{marginBottom: '20px'}}>
            <SectionHeader title='⚗️ MACROSCOPIC ANALYSIS' />
            <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
              <thead>
                <tr style={{backgroundColor: '#f0f0f0'}}>
                  <th style={{padding: '6px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                  <th style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>Patient Value</th>
                  <th style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>WHO Reference</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>pH</td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='pH'
                      value={valueChange.pH || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.pH}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>≥ 7.2</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Appearance</td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='appearance'
                      value={valueChange.appearance || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.appearance}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>Grey-opalescent</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Color</td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='color'
                      value={valueChange.color || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.color}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>Whitish-grey</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Homogeneity</td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='homogeneity'
                      value={valueChange.homogeneity || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.homogeneity}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>Homogenous</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Complete Sample</td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='completeSample'
                      value={valueChange.completeSample || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.completeSample}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>Yes / No</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Liquefaction Time (min)</td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='liquefactionTime'
                      value={valueChange.liquefactionTime || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.liquefactionTime}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>≤ 60 min</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Time of Collection</td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    {!show ? (
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', paddingLeft: '15px'}}>
                        <input
                          type='number'
                          min='1'
                          max='12'
                          placeholder='HH'
                          name='timeOfCollectionHour'
                          value={valueChange.timeOfCollectionHour || ''}
                          onChange={handleChange}
                          style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                        />
                        <span>:</span>
                        <input
                          type='number'
                          min='0'
                          max='59'
                          placeholder='MM'
                          name='timeOfCollectionMinute'
                          value={valueChange.timeOfCollectionMinute || ''}
                          onChange={handleChange}
                          style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                        />
                        <select name='timeOfCollectionPeriod' value={valueChange.timeOfCollectionPeriod || ''} onChange={handleChange} style={{...commonStyles.input, width: '50px'}}>
                          <option value='AM'>AM</option>
                          <option value='PM'>PM</option>
                        </select>
                      </div>
                    ) : (
                      <span>
                        {valueChange.timeOfCollectionHour && valueChange.timeOfCollectionMinute && valueChange.timeOfCollectionPeriod
                          ? `${valueChange.timeOfCollectionHour}:${valueChange.timeOfCollectionMinute.toString().padStart(2, '0')} ${valueChange.timeOfCollectionPeriod}`
                          : ''}
                      </span>
                    )}
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Time of Examination</td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    {!show ? (
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', paddingLeft: '15px'}}>
                        <input
                          type='number'
                          min='1'
                          max='12'
                          placeholder='HH'
                          name='timeOfExaminationHour'
                          value={valueChange.timeOfExaminationHour || ''}
                          onChange={handleChange}
                          style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                        />
                        <span>:</span>
                        <input
                          type='number'
                          min='0'
                          max='59'
                          placeholder='MM'
                          name='timeOfExaminationMinute'
                          value={valueChange.timeOfExaminationMinute || ''}
                          onChange={handleChange}
                          style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                        />
                        <select name='timeOfExaminationPeriod' value={valueChange.timeOfExaminationPeriod || ''} onChange={handleChange} style={{...commonStyles.input, width: '50px'}}>
                          <option value='AM'>AM</option>
                          <option value='PM'>PM</option>
                        </select>
                      </div>
                    ) : (
                      <span>
                        {valueChange.timeOfExaminationHour && valueChange.timeOfExaminationMinute && valueChange.timeOfExaminationPeriod
                          ? `${valueChange.timeOfExaminationHour}:${valueChange.timeOfExaminationMinute.toString().padStart(2, '0')} ${valueChange.timeOfExaminationPeriod}`
                          : ''}
                      </span>
                    )}
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Microscopic Analysis */}
          <div style={{marginBottom: '20px'}}>
            <SectionHeader title='🔬 MICROSCOPIC ANALYSIS' />
            <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
              <thead>
                <tr style={{backgroundColor: '#f0f0f0'}}>
                  <th style={{padding: '6px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                  {/* <th style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>PRE WASH</th> */}
                  <th style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>POST WASH</th>
                  <th style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>WHO Reference</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Volume (mL)</td>
                  {/*  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='volume'
                      value={valueChange.volume || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.volume}</span>
                  </td> */}
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='volumePost'
                      value={valueChange.volumePost || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.volumePost}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>&ge; 1.4mL</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Sperm Concentration (mill/mL)</td>
                  {/* <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='concentration'
                      value={valueChange.concentration || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.concentration}</span>
                  </td> */}
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='concentrationPost'
                      value={valueChange.concentrationPost || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.concentrationPost}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>&ge; 16 million/mL</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Total Sperm Count (million)</td>
                  {/* <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='total'
                      value={valueChange.total || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.total}</span>
                  </td> */}
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='totalPost'
                      value={valueChange.totalPost || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.totalPost}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>&ge; 39 million</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Progressive Motility (%)</td>
                  {/* <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='progressiveMotility'
                      value={valueChange.progressiveMotility || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.progressiveMotility}</span>
                  </td> */}
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='progressiveMotilityPost'
                      value={valueChange.progressiveMotilityPost || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.progressiveMotilityPost}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>&ge; 30%</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Non-Progressive Motility (%)</td>
                  {/* <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='nonProgressiveMotile'
                      value={valueChange.nonProgressiveMotile || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.nonProgressiveMotile}</span>
                  </td> */}
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='nonProgressiveMotilePost'
                      value={valueChange.nonProgressiveMotilePost || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.nonProgressiveMotilePost}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>1%</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Immotile (%)</td>
                  {/*  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='immotile'
                      value={valueChange.immotile || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.immotile}</span>
                  </td> */}
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='immotilePost'
                      value={valueChange.immotilePost || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.immotilePost}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>20%</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Total Motile Sperm Count (TMSC)</td>
                  {/* <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='totalMotileSpermCount'
                      value={valueChange.totalMotileSpermCount || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.totalMotileSpermCount}</span>
                  </td> */}
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='totalMotileSpermCountPost'
                      value={valueChange.totalMotileSpermCountPost || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.totalMotileSpermCountPost}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=42%`}</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Morphology (Strict Criteria)</td>
                  {/*  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='morphology'
                      value={valueChange.morphology || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.morphology}</span>
                  </td> */}
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <input
                      type='text'
                      className={`createTime ${show ? 'hide' : 'show'}`}
                      name='morphologyPost'
                      value={valueChange.morphologyPost || ''}
                      onChange={e => handleChange(e)}
                      style={{...commonStyles.input, width: '150px'}}
                    />
                    <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.morphologyPost}</span>
                  </td>
                  <td style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>&ge; 4% normal forms</td>
                </tr>
                <tr>
                  <td style={{padding: '6px', border: '1px solid #ddd', fontWeight: 600}}>Processing Method</td>
                  <td colSpan='1' style={{padding: '6px', border: '1px solid #ddd', textAlign: 'center'}}>
                    <select name='methodType' value={valueChange.methodType} onChange={handleChange} style={{...commonStyles.input, width: '150px'}} className={show ? 'hide' : 'show'}>
                      {methodOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span style={{marginRight: '10px'}} className={`input ${!show ? 'hide' : 'show'}`}>
                      {valueChange.methodType}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Diagnosis */}
          <div style={{marginBottom: '10px'}}>
            <div style={{margin: '8px 0', fontSize: '16px'}}>
              <SectionHeader title='📋 DIAGNOSIS' inline={true} />
            </div>
            {!show ? (
              <textarea
                name='diagnosis'
                value={valueChange.diagnosis}
                onChange={handleChange}
                style={{
                  ...commonStyles.input,
                  width: '100%',
                  minHeight: '35px',
                  padding: '6px',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '12px',
                  marginTop: '3px'
                }}
                placeholder='Enter clinical diagnosis and notes...'
              />
            ) : (
              valueChange.diagnosis && (
                <div
                  style={{
                    border: '1px solid #ddd',
                    padding: '6px',
                    minHeight: '25px',
                    backgroundColor: '#f9f9f9',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '12px',
                    marginTop: '3px'
                  }}>
                  {valueChange.diagnosis}
                </div>
              )
            )}
          </div>
          <div
            style={{
              overflowX: 'hidden',
              maxWidth: '97%'
              // margin: "64px auto 32px auto",
            }}>
            <div
              style={{
                padding: '16px 8px 16px 8px'
              }}>
              <span
                style={{
                  marginLeft: '65%',
                  display: 'block',
                  marginBottom: '16px',
                  fontWeight: '600'
                }}>
                <pre style={{fontFamily: 'system-ui', fontWeight: 500}}>Clinician Signature Andrologist Signature</pre>
              </span>
              <span
                style={{
                  display: 'block',
                  marginBottom: '16px',
                  fontSize: '12px'
                }}>
                <strong>*Percentage respect to total number of abnormal spermatoza</strong>
              </span>
              <span style={{display: 'block', fontSize: '12px'}}>
                New reference values 17-01-2011: Pursuant to the information published by WHO in the Human Reproduction Update, Vol.1.6, No3 pp. 231-245, 2010
              </span>
            </div>
          </div>
        </div>
        {/* </div> */}
        {/* <p style={{textAlign:"center",padding:"30px 10px"}}>x x x End Of Bill x x x</p> */}

        <div className='Footer'>
          <button onClick={handletest} className='Button_Create'>
            Add Report
          </button>
        </div>
      </div>
    </>
  );
};

export default IUIReport;
